/**
 * FavouritesWidget - Star icon dropdown for managing favourite pages
 * Displays filled/empty star, dropdown with add/remove current page + list of favourites
 */

import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
  Input,
} from '@/components/ui';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { Star, X } from 'lucide-react';
import { useFavouritesWidget } from './FavouritesWidget.hook';
import { navigatePortalInternal } from '../PortalLayout/PortalLayout.store';

export interface FavouritesWidgetProps {
  className?: string;
}

export function FavouritesWidget({ className }: FavouritesWidgetProps) {
  const { t } = useTranslation(NS.COMMON);
  const {
    favourites,
    loading,
    isCurrentPageFavourited,
    currentUrl,
    addFavourite,
    removeFavourite,
  } = useFavouritesWidget();

  const [customName, setCustomName] = useState(document.title);

  const handleAddCurrentPage = () => {
    addFavourite(customName.trim() || document.title, currentUrl);
  };

  const handleRemoveCurrentPage = () => {
    removeFavourite(currentUrl);
  };

  const handleRemoveFavourite = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    e.stopPropagation();
    removeFavourite(url);
  };

  const handleFavouriteClick = (url: string) => {
    navigatePortalInternal(url);
  };

  // Sort favourites alphabetically by name
  const sortedFavourites = [...favourites].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={className ?? 'relative h-8 w-8 p-0'}
          aria-label={t('FavouritesWidget_StarLabel', { defaultValue: 'Favourite Pages' })}
        >
          {isCurrentPageFavourited ? (
            <Star className="h-5 w-5 fill-current text-yellow-500" />
          ) : (
            <Star className="h-5 w-5" />
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-72" align="end">
        {/* Add/Remove current page section */}
        {isCurrentPageFavourited ? (
          <div className="px-3 py-2">
            <button
              onClick={handleRemoveCurrentPage}
              className="text-sm text-destructive hover:underline cursor-pointer"
            >
              {t('FavouritesWidget_RemoveThisPage', {
                defaultValue: 'Remove this page from your favorites list',
              })}
            </button>
          </div>
        ) : (
          <div className="px-3 py-2 space-y-2">
            <Input
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder={document.title}
              className="text-sm"
            />
            <button
              onClick={handleAddCurrentPage}
              className="w-full text-sm text-primary hover:underline cursor-pointer text-left"
            >
              {t('FavouritesWidget_AddThisPage', {
                defaultValue: 'Add this page to your favorites list',
              })}
            </button>
          </div>
        )}

        <DropdownMenuSeparator />

        {/* Loading state */}
        {loading && (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            {t('FavouritesWidget_Loading', { defaultValue: 'Loading...' })}
          </div>
        )}

        {/* Empty state */}
        {!loading && favourites.length === 0 && (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            {t('FavouritesWidget_Empty', { defaultValue: 'No favourite pages' })}
          </div>
        )}

        {/* Favourites list */}
        {!loading &&
          sortedFavourites.map((fav) => (
            <DropdownMenuItem
              key={fav.url}
              onClick={() => handleFavouriteClick(fav.url)}
              className="cursor-pointer flex items-center justify-between gap-2"
            >
              <span className="truncate text-sm">{fav.name}</span>
              <button
                onClick={(e) => handleRemoveFavourite(e, fav.url)}
                className="shrink-0 text-muted-foreground hover:text-destructive"
                aria-label={t('FavouritesWidget_RemoveFavourite_AriaLabel', { defaultValue: 'Remove' })}
              >
                <X className="h-4 w-4" />
              </button>
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
